// ─────────────────────────────────────────────────────────────────────────────
// AliasesEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for product aliases CRUD operations.
// Responsibilities:
//   - Load paginated aliases list from API
//   - Create, update, delete, and bulk-delete aliases
//   - Manage loading state via SetAliasesLoader
//   - Show toast notifications on success/error
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { AliasService } from '../services/alias.service';
import { IProductAlias } from '../models/alias.models';
import * as aliasesActions from './aliases.actions';

@Injectable()
export class AliasesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private aliasService: AliasService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load aliases ──────────────────────────────────────────────────────────

  LoadAliases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aliasesActions.LoadAliases),
      switchMap((action) =>
        this.aliasService.listAliases(action.page, action.key, action.filters, action.sortBy, action.sortMode).pipe(
          map((res: APIResponse<IProductAlias>) => aliasesActions.LoadAliasesSuccess({ response: res })),
          catchError(() => of(aliasesActions.LoadAliasesError())),
        ),
      ),
    ),
  );

  // ── Create alias ──────────────────────────────────────────────────────────

  CreateAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aliasesActions.CreateAlias),
      tap(() => {
        this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.aliasService.createAlias(action.payload).pipe(
          map((res: APIResponse<IProductAlias>) => aliasesActions.CreateAliasSuccess({ alias: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.aliases.toast.create_success'), 'SUCCESS');
          }),
          catchError(() => of(aliasesActions.CreateAliasError())),
          finalize(() => {
            this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Update alias ──────────────────────────────────────────────────────────

  UpdateAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aliasesActions.UpdateAlias),
      tap(() => {
        this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.aliasService.updateAlias(action.id, action.payload).pipe(
          map((res: APIResponse<IProductAlias>) => aliasesActions.UpdateAliasSuccess({ alias: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.aliases.toast.update_success'), 'SUCCESS');
          }),
          catchError(() => of(aliasesActions.UpdateAliasError())),
          finalize(() => {
            this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Delete alias ──────────────────────────────────────────────────────────

  DeleteAlias$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aliasesActions.DeleteAlias),
      tap(() => {
        this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.aliasService.deleteAlias(action.id).pipe(
          map(() => aliasesActions.DeleteAliasSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.aliases.toast.delete_success'), 'SUCCESS');
          }),
          catchError(() => of(aliasesActions.DeleteAliasError())),
          finalize(() => {
            this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk delete aliases ───────────────────────────────────────────────────

  BulkDeleteAliases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aliasesActions.BulkDeleteAliases),
      tap(() => {
        this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.aliasService.bulkDeleteAliases(action.ids).pipe(
          map(() => aliasesActions.BulkDeleteAliasesSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.aliases.toast.bulk_delete_success'), 'SUCCESS');
          }),
          catchError(() => of(aliasesActions.BulkDeleteAliasesError())),
          finalize(() => {
            this.store.dispatch(aliasesActions.SetAliasesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );
}
